import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Send, CheckCircle, RefreshCw, MessageSquare, ChevronDown, Calendar } from "lucide-react";
import { toast } from "sonner";
import { SimplePhotoGrid } from "@/components/owner/SubmissionPhotoGallery";
import { PropertyAvailabilityCalendar } from "@/components/vendor/PropertyAvailabilityCalendar";
import { VisitSchedulingPanel } from "@/components/vendor/VisitSchedulingPanel";
import type { WorkOrderReservation } from "@/lib/workOrderReservations";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

export interface WorkOrderPayload {
  issue: {
    id: string;
    room_name: string;
    description: string;
    photo_url: string | null;
    severity: string;
    vendor_status: string;
    vendor_scheduled_start: string | null;
    vendor_scheduled_end: string | null;
    vendor_requested_start: string | null;
    vendor_requested_end: string | null;
    vendor_visit_request_denied_at: string | null;
    vendor_quote_amount: number | null;
    vendor_quote_notes: string | null;
    created_at: string;
  };
  property: { id: string; name: string; address: string | null; timezone?: string | null };
  vendor: { id: string; business_name: string; phone: string | null; email: string | null } | null;
  messages: Array<{
    id: string;
    sender_type: string;
    sender_user_id: string | null;
    message_text: string;
    created_at: string;
  }>;
}

async function fetchWorkOrder(token: string): Promise<WorkOrderPayload | null> {
  const { data, error } = await supabase.rpc("get_work_order_by_token", {
    issue_token: token.trim(),
  });
  if (error) throw error;
  return data as WorkOrderPayload | null;
}

async function postVendorMessage(token: string, text: string): Promise<string> {
  const { data, error } = await supabase.rpc("work_order_post_message_vendor", {
    issue_token: token.trim(),
    message_text_in: text.trim(),
  });
  if (error) throw error;
  return data as string;
}

async function callVendorAi(
  token: string,
  body: { trigger: "init" | "message" | "schedule_visit" | "quote_from_photos"; message_text?: string }
): Promise<{ message_text?: string; proposed_start_iso?: string; proposed_end_iso?: string }> {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) throw new Error("Supabase not configured");
  const res = await fetch(`${SUPABASE_URL}/functions/v1/vendor-work-order-ai`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({ issue_token: token, ...body }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error ?? "AI request failed");
  return json;
}

async function confirmVisit(
  token: string,
  startIso: string,
  endIso: string
): Promise<{ ok: boolean; error?: string }> {
  const { data, error } = await supabase.rpc("work_order_confirm_visit", {
    issue_token: token.trim(),
    start_iso: startIso,
    end_iso: endIso,
  });
  if (error) throw error;
  const result = (data as { ok?: boolean; error?: string }) ?? {};
  return { ok: result.ok === true, error: result.error };
}

async function fetchReservations(token: string): Promise<WorkOrderReservation[]> {
  const { data, error } = await supabase.rpc("get_reservations_for_work_order", {
    issue_token: token.trim(),
    days_ahead: 90,
  });
  if (error) throw error;
  return (data as WorkOrderReservation[]) ?? [];
}

async function requestConflictingVisitTime(
  token: string,
  requestedStart: string,
  requestedEnd: string
): Promise<{
  ok: boolean;
  error?: string;
  issueId?: string;
  propertyId?: string;
  sms?: {
    ownerPhone?: string | null;
    vendorName?: string;
    propertyName?: string;
    requestedDate?: string;
  };
}> {
  const { data, error } = await supabase.rpc("request_conflicting_visit_time", {
    issue_token: token.trim(),
    requested_start: requestedStart,
    requested_end: requestedEnd,
  });
  if (error) throw error;
  const result = (data as {
    ok?: boolean;
    error?: string;
    issue_id?: string;
    property_id?: string;
    sms?: {
      ownerPhone?: string | null;
      vendorName?: string;
      propertyName?: string;
      requestedDate?: string;
    };
  }) ?? {};
  return {
    ok: result.ok === true,
    error: result.error,
    issueId: result.issue_id,
    propertyId: result.property_id,
    sms: result.sms,
  };
}

async function sendHostConflictAlert(params: {
  ownerPhone: string;
  vendorName: string;
  propertyName: string;
  requestedDate: string;
  issueUrl: string;
}): Promise<void> {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) return;
  const res = await fetch(`${SUPABASE_URL}/functions/v1/send-host-conflict-alert`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify(params),
  });
  const json = await res.json().catch(() => ({}));
  if (!json.success) {
    console.warn("[send-host-conflict-alert]", json.error);
  }
}

const severityLabel: Record<string, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  urgent: "Urgent",
};

const vendorStatusLabel: Record<string, string> = {
  unassigned: "Unassigned",
  awaiting_vendor: "Awaiting vendor",
  scheduled: "Scheduled",
  in_progress: "In progress",
  completed: "Completed",
  pending_host_approval: "Pending host approval",
};

const INTRO_MESSAGE_PREFIX = "Hi! This is a work order for a property we manage.";
function senderLabel(msg: { sender_type: string; message_text: string }): string {
  if (msg.sender_type === "vendor") return "Vendor";
  if (msg.sender_type === "owner") return "Owner";
  if (msg.sender_type === "team") return "Team";
  if (msg.sender_type === "ai" && msg.message_text.startsWith(INTRO_MESSAGE_PREFIX)) return "Stravo";
  return "Stravo AI";
}

export default function VendorWorkOrderPage() {
  const { token } = useParams<{ token: string }>();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState("");
  const [proposedSlot, setProposedSlot] = useState<{ start_iso: string; end_iso: string } | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: workOrder, isLoading, error } = useQuery({
    queryKey: ["work-order", token],
    queryFn: () => fetchWorkOrder(token!),
    enabled: !!token,
    refetchInterval: (data) => {
      const s = data?.issue?.vendor_status;
      return s === "scheduled" ? false : 8000;
    },
  });

  const { data: reservations = [], isLoading: reservationsLoading } = useQuery({
    queryKey: ["work-order-reservations", token],
    queryFn: () => fetchReservations(token!),
    enabled: !!token,
    staleTime: 60_000,
  });

  const postMessage = useMutation({
    mutationFn: (text: string) => postVendorMessage(token!, text),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["work-order", token] });
    },
  });

  const confirmVisitMutation = useMutation({
    mutationFn: ({ startIso, endIso }: { startIso: string; endIso: string }) =>
      confirmVisit(token!, startIso, endIso),
    onSuccess: (result) => {
      if (result.ok) {
        toast.success("Visit scheduled.");
        setProposedSlot(null);
        queryClient.invalidateQueries({ queryKey: ["work-order", token] });
      } else toast.error(result.error ?? "Failed to schedule");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to schedule"),
  });

  const requestConflictMutation = useMutation({
    mutationFn: ({ startIso, endIso }: { startIso: string; endIso: string }) =>
      requestConflictingVisitTime(token!, startIso, endIso),
    onSuccess: async (result) => {
      if (!result.ok) {
        toast.error(result.error ?? "Failed to send request");
        return;
      }
      toast.success("Request sent — the host has been notified and will respond shortly.");
      queryClient.invalidateQueries({ queryKey: ["work-order", token] });
      const sms = result.sms;
      if (sms?.ownerPhone?.trim() && result.issueId && result.propertyId) {
        const base =
          typeof window !== "undefined" ? window.location.origin : "https://portal.stravo.ai";
        const issueUrl = `${base}/properties/${result.propertyId}?tab=maintenance&focusIssueId=${result.issueId}`;
        await sendHostConflictAlert({
          ownerPhone: sms.ownerPhone.trim(),
          vendorName: sms.vendorName ?? "Vendor",
          propertyName: sms.propertyName ?? "Property",
          requestedDate: sms.requestedDate ?? "",
          issueUrl,
        });
      }
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to send request"),
  });

  const triggerAi = async (
    trigger: "init" | "message" | "schedule_visit" | "quote_from_photos",
    messageText?: string
  ) => {
    if (!token) return;
    setAiLoading(true);
    try {
      const result = await callVendorAi(token, { trigger, message_text: messageText });
      queryClient.invalidateQueries({ queryKey: ["work-order", token] });
      if (result.proposed_start_iso && result.proposed_end_iso)
        setProposedSlot({
          start_iso: result.proposed_start_iso,
          end_iso: result.proposed_end_iso,
        });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [workOrder?.messages?.length, aiLoading]);

  // Auto-post intro + first AI reply when thread is empty (first open)
  const didInit = useRef(false);
  useEffect(() => {
    if (!token) {
      didInit.current = false;
      return;
    }
    if (!workOrder || didInit.current) return;
    if (workOrder.messages?.length === 0) {
      didInit.current = true;
      triggerAi("init");
    }
  }, [token, workOrder?.messages?.length]);

  useEffect(() => {
    if (workOrder?.issue?.vendor_status === "scheduled" && workOrder.issue.vendor_scheduled_start) {
      setCalendarOpen(false);
    }
  }, [workOrder?.issue?.vendor_status, workOrder?.issue?.vendor_scheduled_start]);

  if (!token) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <p className="text-muted-foreground text-center">Invalid link.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
        <div className="animate-pulse text-muted-foreground">Loading work order…</div>
      </div>
    );
  }

  if (error || workOrder == null) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">Link invalid or expired.</p>
            <p className="text-center text-xs text-muted-foreground mt-2">
              Ask the property for a new link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { issue, property, messages = [] } = workOrder;
  const propTz = property?.timezone && property.timezone.trim() ? property.timezone : undefined;

  const formatInPropTz = (iso: string, opts: Intl.DateTimeFormatOptions = { dateStyle: "short", timeStyle: "short" }) =>
    new Date(iso).toLocaleString(undefined, { ...opts, timeZone: propTz ?? undefined });

  const handleSend = async () => {
    const text = message.trim();
    if (!text) return;
    const lastWasOwnerOrTeam = messages.length > 0 && (messages[messages.length - 1].sender_type === "owner" || messages[messages.length - 1].sender_type === "team");
    try {
      await postMessage.mutateAsync(text);
      setMessage("");
      // Only trigger AI when vendor is replying to someone other than owner/team (e.g. AI or vendor). When owner/team just replied, only they (or owner turning AI on) can respond.
      if (!lastWasOwnerOrTeam) {
        await triggerAi("message", text);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to send");
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 pb-8">
      <div className="max-w-lg mx-auto px-4 pt-6 space-y-4">
        <Card>
          <CardHeader className="pb-2">
            <h1 className="text-xl font-semibold">Stravo Work Order</h1>
            <p className="text-sm text-muted-foreground">{property.name}</p>
            {property.address && (
              <p className="text-xs text-muted-foreground">{property.address}</p>
            )}
          </CardHeader>
        </Card>

        <Card>
          <CardContent className="pt-6 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium">{issue.room_name}</span>
              <Badge variant="secondary">{severityLabel[issue.severity] ?? issue.severity}</Badge>
              <Badge variant="outline">{vendorStatusLabel[issue.vendor_status] ?? issue.vendor_status}</Badge>
            </div>
            <p className="text-sm text-muted-foreground">{issue.description}</p>
            {issue.photo_url && (
              <SimplePhotoGrid
                photos={[{ id: issue.id, photo_url: issue.photo_url }]}
                className="rounded-lg overflow-hidden"
              />
            )}
            {issue.vendor_scheduled_start && (
              <p className="text-sm text-muted-foreground">
                Scheduled: {new Date(issue.vendor_scheduled_start).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                {issue.vendor_scheduled_end
                  ? ` – ${new Date(issue.vendor_scheduled_end).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                  : ""}
              </p>
            )}
            {issue.vendor_quote_amount != null && issue.vendor_quote_amount > 0 && (
              <p className="text-sm">
                Quote: ${Number(issue.vendor_quote_amount).toFixed(2)}
                {issue.vendor_quote_notes ? ` — ${issue.vendor_quote_notes}` : ""}
              </p>
            )}
          </CardContent>
        </Card>

        {issue.vendor_visit_request_denied_at && issue.vendor_status !== "scheduled" && (
          <Card className="border-muted">
            <CardContent className="pt-4 pb-4">
              <p className="text-sm text-muted-foreground">
                The host wasn&apos;t able to accommodate that time. Please choose a different date
                from the available slots below.
              </p>
            </CardContent>
          </Card>
        )}

        {issue.vendor_status === "pending_host_approval" && (
          <Card className="border-amber-400 bg-amber-50">
            <CardContent className="pt-4 pb-4">
              <p className="text-sm text-amber-900 font-medium">
                Request sent — the host has been notified and will respond shortly.
              </p>
              {issue.vendor_requested_start && (
                <p className="text-xs text-amber-800 mt-1">
                  Requested: {formatInPropTz(issue.vendor_requested_start)}
                  {issue.vendor_requested_end
                    ? ` – ${formatInPropTz(issue.vendor_requested_end, { hour: "2-digit", minute: "2-digit" })}`
                    : ""}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="max-h-[380px] overflow-hidden flex flex-col">
          <Collapsible open={calendarOpen} onOpenChange={setCalendarOpen}>
            <CollapsibleTrigger className="flex w-full items-center justify-between px-6 py-3 text-sm font-medium hover:bg-muted/50">
              <span className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Property availability
              </span>
              <ChevronDown
                className={`h-4 w-4 transition-transform ${calendarOpen ? "rotate-180" : ""}`}
              />
            </CollapsibleTrigger>
            <CollapsibleContent className="px-6 pb-4 overflow-y-auto">
              {reservationsLoading ? (
                <div className="animate-pulse text-sm text-muted-foreground py-6 text-center">
                  Loading calendar…
                </div>
              ) : (
                <PropertyAvailabilityCalendar reservations={reservations} />
              )}
            </CollapsibleContent>
          </Collapsible>
        </Card>

        {issue.vendor_status !== "scheduled" &&
          issue.vendor_status !== "pending_host_approval" &&
          issue.vendor_status !== "completed" && (
            <Card>
              <CardContent className="pt-4 pb-4">
                <VisitSchedulingPanel
                  severity={issue.severity}
                  propertyTimezone={propTz}
                  reservations={reservations}
                  onConfirm={(startIso, endIso) =>
                    confirmVisitMutation.mutate({ startIso, endIso })
                  }
                  onRequestConflict={(startIso, endIso) =>
                    requestConflictMutation.mutate({ startIso, endIso })
                  }
                  confirmPending={confirmVisitMutation.isPending}
                  requestPending={requestConflictMutation.isPending}
                />
              </CardContent>
            </Card>
          )}

        {/* Chat */}
        <Card>
          <CardHeader className="py-3">
            <h2 className="text-sm font-medium flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Chat — reply here to coordinate. No login required.
            </h2>
          </CardHeader>
          <CardContent className="space-y-3">
            {messages.length === 0 && !aiLoading && (
              <p className="text-sm text-muted-foreground text-center py-2">Loading conversation…</p>
            )}
            <div className="max-h-72 overflow-y-auto space-y-2 rounded-lg bg-muted/50 p-2">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`text-sm p-2 rounded-md max-w-[90%] ${
                    msg.sender_type === "vendor"
                      ? "bg-primary/10 ml-0 mr-auto"
                      : msg.sender_type === "ai"
                        ? "bg-muted ml-auto mr-0"
                        : "bg-background border ml-auto mr-0"
                  }`}
                >
                  <span className="text-xs text-muted-foreground font-medium">
                    {senderLabel(msg)}
                  </span>
                  <p className="mt-0.5 break-words whitespace-pre-wrap">{msg.message_text}</p>
                </div>
              ))}
              {aiLoading && (
                <div className="text-sm p-2 rounded-md max-w-[90%] bg-muted ml-auto mr-0">
                  <span className="text-xs text-muted-foreground font-medium">Stravo AI</span>
                  <p className="mt-0.5 text-muted-foreground italic">Stravo AI is typing…</p>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="flex flex-wrap gap-2 items-center">
              {proposedSlot && (
                <>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() =>
                      confirmVisitMutation.mutate({
                        startIso: proposedSlot.start_iso,
                        endIso: proposedSlot.end_iso,
                      })
                    }
                    disabled={confirmVisitMutation.isPending}
                  >
                    <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                    Confirm time
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    {formatInPropTz(proposedSlot.start_iso)} – {formatInPropTz(proposedSlot.end_iso, { hour: "2-digit", minute: "2-digit" })}
                    {propTz && ` (${propTz.replace(/_/g, " ")})`}
                  </span>
                </>
              )}
              {proposedSlot && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setProposedSlot(null)}
                >
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                  Request different time
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Textarea
                placeholder="Type a message…"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="min-h-[56px] resize-none"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
              />
              <Button
                type="button"
                size="icon"
                className="shrink-0"
                onClick={handleSend}
                disabled={!message.trim() || postMessage.isPending}
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
